import React, { createContext, useContext, useReducer, useEffect, useState, useCallback } from 'react';

const CartContext = createContext();

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TO_CART':
      const product = action.payload;
      // Create a unique key for cart items based on product id and variation
      // If product has variation_id, use it; otherwise use combination of color/size
      const getItemKey = (item) => {
        if (item.variation_id) return `${item.id}_${item.variation_id}`;
        if (item.variation?.id) return `${item.id}_${item.variation.id}`;
        // Use color and size to differentiate variations
        const color = item.selectedColor || item.variation?.color || '';
        const size = item.selectedSize || item.variation?.size || '';
        return `${item.id}_${color}_${size}`;
      };
      
      const newItemKey = getItemKey(product);
      const existingItem = state.items.find(item => getItemKey(item) === newItemKey);
      
      if (existingItem) {
        // Item exists, update quantity (use provided quantity or add 1)
        const quantityToAdd = product.quantity || 1;
        const updatedItems = state.items.map(item =>
          getItemKey(item) === newItemKey
            ? { ...item, quantity: item.quantity + quantityToAdd }
            : item
        );
        console.log('CartContext - Updated existing item. New cart:', updatedItems);
        return {
          ...state,
          items: updatedItems
        };
      } else {
        // New item, add to cart (use provided quantity or default to 1)
        const quantity = product.quantity || 1;
        const newItems = [...state.items, { ...product, quantity }];
        console.log('CartContext - Added new item. New cart:', newItems);
        return {
          ...state,
          items: newItems
        };
      }
    
    case 'REMOVE_FROM_CART':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };
    
    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        )
      };
    
    case 'CLEAR_CART':
      return {
        ...state,
        items: []
      };
    
    case 'SET_CART':
      return {
        ...state,
        items: action.payload
      };
    
    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, {
    items: []
  });
  const [cartLoading, setCartLoading] = useState(true);

  // Load cart from localStorage on mount
  useEffect(() => {
    const loadCart = () => {
      const savedCart = localStorage.getItem('cart');
      console.log('CartContext - Loading cart from localStorage:', savedCart);
      if (savedCart) {
        try {
          const cartItems = JSON.parse(savedCart);
          console.log('CartContext - Parsed cart items:', cartItems);
          console.log('CartContext - Cart items count:', cartItems?.length || 0);
          if (Array.isArray(cartItems) && cartItems.length > 0) {
            console.log('CartContext - First item structure:', cartItems[0]);
            dispatch({ type: 'SET_CART', payload: cartItems });
            console.log('CartContext - Cart loaded successfully, items:', cartItems);
          } else {
            console.log('CartContext - Cart is empty or not an array');
            dispatch({ type: 'SET_CART', payload: [] });
          }
        } catch (error) {
          console.error('Error loading cart from localStorage:', error);
          // Clear corrupted cart data
          localStorage.removeItem('cart');
          dispatch({ type: 'SET_CART', payload: [] });
        }
      } else {
        console.log('CartContext - No saved cart found in localStorage');
        dispatch({ type: 'SET_CART', payload: [] });
      }
      setCartLoading(false);
    };
    
    loadCart();
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    // Don't save during initial load (when cartLoading is true)
    if (!cartLoading) {
      console.log('CartContext - Saving cart to localStorage:', state.items);
      console.log('CartContext - Items count to save:', state.items?.length || 0);
      localStorage.setItem('cart', JSON.stringify(state.items));
    }
  }, [state.items, cartLoading]);

  // Listen for storage events and also manually check for changes
  useEffect(() => {
    if (cartLoading) return;
    
    // Create a custom event listener for localStorage changes in the same tab
    const checkCart = () => {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        try {
          const cartItems = JSON.parse(savedCart);
          if (Array.isArray(cartItems) && cartItems.length !== state.items.length) {
            console.log('CartContext - Manual check: localStorage changed, syncing...');
            dispatch({ type: 'SET_CART', payload: cartItems });
          }
        } catch (error) {
          console.error('CartContext - Error in manual check:', error);
        }
      }
    };

    // Check immediately
    checkCart();
    
    // Check every 500ms
    const interval = setInterval(checkCart, 500);
    
    return () => clearInterval(interval);
  }, [cartLoading, state.items.length]);

  const addToCart = (product) => {
    console.log('CartContext - Adding to cart:', product);
    dispatch({ type: 'ADD_TO_CART', payload: product });
  };

  const removeFromCart = (productId) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: productId });
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } });
    }
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const getTotalItems = () => {
    return state.items.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return state.items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  // Sync function to reload cart from localStorage if there's a mismatch
  const syncCartFromStorage = useCallback(() => {
    if (cartLoading) {
      return false; // Don't sync while loading
    }
    
    const savedCart = localStorage.getItem('cart');
    
    if (savedCart) {
      try {
        const cartItems = JSON.parse(savedCart);
        if (Array.isArray(cartItems)) {
          // Only sync if localStorage has items but state doesn't, or if counts differ
          if (cartItems.length > 0 && (state.items.length === 0 || cartItems.length !== state.items.length)) {
            dispatch({ type: 'SET_CART', payload: cartItems });
            return true; // Indicate sync happened
          }
        }
      } catch (error) {
        console.error('CartContext - Error syncing cart:', error);
      }
    } else {
      console.log('CartContext - No cart in localStorage to sync');
    }
    return false; // No sync needed
  }, [cartLoading, state.items.length]);

  // Periodically check for cart sync (every 1 second) - helps catch race conditions
  useEffect(() => {
    if (!cartLoading) {
      // Immediate sync check
      syncCartFromStorage();

      // Then check periodically (but don't depend on syncCartFromStorage to avoid infinite loop)
      const syncInterval = setInterval(() => {
        // Re-check localStorage directly to avoid dependency issues
        const savedCart = localStorage.getItem('cart');
        if (savedCart) {
          try {
            const cartItems = JSON.parse(savedCart);
            if (Array.isArray(cartItems)) {
              // Only sync if localStorage has items but state doesn't, or if counts differ significantly
              if (cartItems.length > 0 && (state.items.length === 0 || Math.abs(cartItems.length - state.items.length) > 1)) {
                dispatch({ type: 'SET_CART', payload: cartItems });
              }
            }
          } catch (error) {
            // Silent error handling for periodic checks
          }
        }
      }, 2000); // Check every 2 seconds instead of 1

      return () => clearInterval(syncInterval);
    }
  }, [cartLoading]); // Remove syncCartFromStorage dependency to prevent infinite loop

  const value = {
    items: state.items,
    cartLoading,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalItems,
    getTotalPrice,
    syncCartFromStorage
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
